/**
 * EventMarkers — renders event markers on the map.
 *
 * Fetches events from the API based on the current viewport bbox,
 * renders them as pulsing markers, and handles click → event details.
 */

import { useEffect, useRef, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import type { BBox, WingEvent } from '@wingman/shared';
import { api } from '../../lib/api.js';
import { useCommandStore } from '../../store/commandStore.js';

interface EventMarkersProps {
  map: maplibregl.Map | null;
  bbox: BBox | null;
  onEventClick: (event: WingEvent) => void;
}

const EVENT_COLORS: Record<string, string> = {
  nightlife: '#e040fb',
  'music': '#ff6d00',
  'food-drink': '#00e676',
  'arts-theatre': '#ffea00',
  'sports-fitness': '#00b0ff',
  'outdoors-parks': '#69f0ae',
  'family-kids': '#ffab40',
  'learning-classes': '#18ffff',
  'community-social': '#ff4081',
  'volunteer-civic': '#f48fb1',
  'markets-shopping': '#b388ff',
  'film-media': '#8c9eff',
  'games-play': '#b2ff59',
  'wellness': '#76ff03',
  'experiences': '#7c4dff',
  'lgbtq': '#ea80fc',
};

function getEventColor(categorySlug: string): string {
  return EVENT_COLORS[categorySlug] || '#00f5ff';
}

function buildEventMarkerElement(event: WingEvent): HTMLElement {
  const color = getEventColor(event.categorySlug);
  const el = document.createElement('div');
  el.className = 'event-marker';
  el.innerHTML = `
    <div class="event-marker-dot" style="background: ${color}; box-shadow: 0 0 12px ${color};"></div>
    <div class="event-marker-pulse" style="border-color: ${color};"></div>
  `;
  return el;
}

export default function EventMarkers({ map, bbox, onEventClick }: EventMarkersProps) {
  const markersRef = useRef<Map<string, maplibregl.Marker>>(new Map());
  const eventsVisible = useCommandStore((s) => s.layerVisibility.events);
  const requestController = useRef<AbortController | null>(null);

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((m) => m.remove());
    markersRef.current.clear();
  }, []);

  // Fetch and render events when bbox changes
  useEffect(() => {
    if (!map || !bbox || !eventsVisible) {
      clearMarkers();
      return;
    }

    requestController.current?.abort();
    const controller = new AbortController();
    requestController.current = controller;

    api.events.getByBBox(bbox, { limit: 100, signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return;

        // Clear old markers
        clearMarkers();

        // Add new markers
        for (const event of res.events) {
          const el = buildEventMarkerElement(event);
          const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
            .setLngLat([event.lng, event.lat])
            .addTo(map);

          el.addEventListener('click', (e) => {
            e.stopPropagation();
            onEventClick(event);
          });

          markersRef.current.set(event.id, marker);
        }
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          console.error('[EventMarkers] Failed to fetch events:', err);
        }
      });

    return () => {
      controller.abort();
      clearMarkers();
    };
  }, [map, bbox, eventsVisible, clearMarkers, onEventClick]);

  // Toggle visibility
  useEffect(() => {
    if (!eventsVisible) {
      clearMarkers();
    }
  }, [eventsVisible, clearMarkers]);

  return null;
}
